import { useCallback, useEffect, useState } from 'react';
import { isFinishableFunction } from 'fym-common/src/utils/singletonPromise';

export enum AsyncStatus {
  Idle = 'idle',
  Pending = 'pending',
  Success = 'success',
  Error = 'error',
}

export enum AsyncMode {
  Immediate = 0,
  NotImmediate = 1,
}

export function useAsync<T, Args extends unknown[], E = Error>(
  asyncFunction: (...args: Args) => Promise<T>,
  immediate: AsyncMode = AsyncMode.Immediate,
  ...initialArgs: Args
) {
  const isFunctionExecuted = isFinishableFunction(asyncFunction);
  const [status, setStatus] = useState<AsyncStatus>(() => {
    if (isFunctionExecuted && asyncFunction.finished) {
      return asyncFunction.error !== undefined ? AsyncStatus.Error : AsyncStatus.Success;
    }
    return AsyncStatus.Idle;
  });
  const [value, setValue] = useState<T | undefined>(() => (isFunctionExecuted ? asyncFunction.lastValue : undefined));
  const [error, setError] = useState<E | undefined>(undefined);

  const execute = useCallback(
    async (...args: Args) => {
      if (isFinishableFunction(asyncFunction)) {
        asyncFunction.reset();
      }
      setStatus(AsyncStatus.Pending);
      setValue(undefined);
      setError(undefined);
      let response: T | undefined;
      try {
        response = await asyncFunction(...args);
        setValue(response);
        setStatus(AsyncStatus.Success);
      } catch (err: any) {
        setError(err);
        setStatus(AsyncStatus.Error);
        throw err;
      }
      return response;
    },
    [asyncFunction]
  );

  useEffect(() => {
    if (immediate === AsyncMode.Immediate) {
      execute(...initialArgs).catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execute, immediate, ...initialArgs]);
  return { execute, status, value, error };
}
